import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Save,
  Key,
  Cpu,
  Database,
  Bot,
  FolderOpen,
  Shield,
  Activity,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { useLlmStore } from '@/stores/llm'
import { useSystemStore } from '@/stores/system'
import { getGatewayApiKey, setGatewayApiKey } from '@/lib/gateway-auth'

const SETTINGS_STORAGE_KEY = 'omtae.dashboard.settings'

type DashboardSettings = {
  defaultModel: string
  temperature: number
  maxTokens: number
  topP: number
  maxSteps: number
  contextTokens: number
  commandTimeout: number
  enableAgents: boolean
  deniedCommandPrefixes: string
  defaultWorkspace: string
  workspaceAllowlist: string
  modelDirs: string
  openaiKey: string
  discordToken: string
  telegramToken: string
  autoStart: boolean
  logLevel: string
  maxLogHistory: number
}

const createDefaultSettings = (defaultModel: string): DashboardSettings => ({
  defaultModel,
  temperature: 0.7,
  maxTokens: 4096,
  topP: 0.9,
  maxSteps: 30,
  contextTokens: 32768,
  commandTimeout: 300,
  enableAgents: true,
  deniedCommandPrefixes: 'rm -rf /,dd if=,mkfs,shutdown,reboot',
  defaultWorkspace: '/home/jay',
  workspaceAllowlist: '/home/jay',
  modelDirs: '',
  openaiKey: '',
  discordToken: '',
  telegramToken: '',
  autoStart: true,
  logLevel: 'info',
  maxLogHistory: 30,
})

const readStoredSettings = (): Partial<DashboardSettings> => {
  if (typeof window === 'undefined') return {}
  const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY)
  if (!raw) return {}
  try {
    return JSON.parse(raw) as Partial<DashboardSettings>
  } catch {
    return {}
  }
}

export default function SettingsPage() {
  const { toast } = useToast()
  const { models, currentModel, setCurrentModel, fetchModels, isLoading: modelsLoading } = useLlmStore()
  const { health, fetchHealth } = useSystemStore()
  const [settings, setSettings] = useState<DashboardSettings>(() => ({
    ...createDefaultSettings(currentModel),
    ...readStoredSettings(),
  }))
  const [gatewayPasscode, setGatewayPasscode] = useState('')

  useEffect(() => {
    void fetchModels()
    void fetchHealth()
  }, [fetchModels, fetchHealth])

  useEffect(() => {
    setGatewayPasscode(getGatewayApiKey())
  }, [])

  useEffect(() => {
    if (!settings.defaultModel) {
      setSettings((prev) => ({ ...prev, defaultModel: currentModel }))
    }
  }, [currentModel, settings.defaultModel])

  const handleSave = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))
      setGatewayApiKey(gatewayPasscode)
    }
    if (settings.defaultModel) {
      setCurrentModel(settings.defaultModel)
    }
    toast({
      title: 'Settings saved',
      description: 'Dashboard configuration updated. Refresh if the gateway passcode changed.',
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure OMTAE-Solutions
          </p>
        </div>
        <Button onClick={handleSave}>
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="llm" className="space-y-6">
        <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full">
          <TabsTrigger value="llm">
            <Cpu className="h-4 w-4 mr-2" />
            LLM
          </TabsTrigger>
          <TabsTrigger value="agent">
            <Bot className="h-4 w-4 mr-2" />
            Agent
          </TabsTrigger>
          <TabsTrigger value="workspace">
            <FolderOpen className="h-4 w-4 mr-2" />
            Workspace
          </TabsTrigger>
          <TabsTrigger value="api">
            <Key className="h-4 w-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="system">
            <Database className="h-4 w-4 mr-2" />
            System
          </TabsTrigger>
        </TabsList>

        {/* LLM Settings */}
        <TabsContent value="llm" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Default Model Settings</CardTitle>
              <CardDescription>
                Configure default parameters for LLM inference
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Default Model</Label>
                <Select
                  value={settings.defaultModel}
                  onValueChange={(v) =>
                    setSettings({ ...settings, defaultModel: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={modelsLoading ? 'Loading models...' : 'Select a model'} />
                  </SelectTrigger>
                  <SelectContent>
                    {models.length > 0 ? (
                      models.map((model) => (
                        <SelectItem key={model.name} value={model.name}>
                          {model.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value={currentModel}>{currentModel || 'No models found'}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Temperature: {settings.temperature}</Label>
                <Slider
                  value={[settings.temperature]}
                  onValueChange={([v]) =>
                    setSettings({ ...settings, temperature: v })
                  }
                  min={0}
                  max={2}
                  step={0.1}
                />
                <p className="text-xs text-muted-foreground">
                  Lower = more deterministic, Higher = more creative
                </p>
              </div>

              <div className="space-y-2">
                <Label>Max Tokens: {settings.maxTokens}</Label>
                <Slider
                  value={[settings.maxTokens]}
                  onValueChange={([v]) =>
                    setSettings({ ...settings, maxTokens: v })
                  }
                  min={256}
                  max={16384}
                  step={256}
                />
              </div>

              <div className="space-y-2">
                <Label>Top P: {settings.topP}</Label>
                <Slider
                  value={[settings.topP]}
                  onValueChange={([v]) => setSettings({ ...settings, topP: v })}
                  min={0}
                  max={1}
                  step={0.05}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agent Settings */}
        <TabsContent value="agent" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Agent Configuration</CardTitle>
              <CardDescription>
                Configure agent execution behavior. These values are stored in your browser and used as defaults by the Chat page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Native Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Full system access, no container sandbox
                  </p>
                </div>
                <Badge variant="outline" className="bg-emerald-600/20 text-emerald-400 border-emerald-500/30">
                  <Shield className="h-3 w-3 mr-1" />
                  Enabled (server-side)
                </Badge>
              </div>

              <div className="space-y-2">
                <Label>Max Steps: {settings.maxSteps}</Label>
                <Slider
                  value={[settings.maxSteps]}
                  onValueChange={([v]) =>
                    setSettings({ ...settings, maxSteps: v })
                  }
                  min={1}
                  max={50}
                  step={1}
                />
                <p className="text-xs text-muted-foreground">
                  Maximum tool calls the agent can make per request
                </p>
              </div>

              <div className="space-y-2">
                <Label>Context Tokens: {settings.contextTokens.toLocaleString()}</Label>
                <Slider
                  value={[settings.contextTokens]}
                  onValueChange={([v]) =>
                    setSettings({ ...settings, contextTokens: v })
                  }
                  min={4096}
                  max={131072}
                  step={4096}
                />
              </div>

              <div className="space-y-2">
                <Label>Command Timeout: {settings.commandTimeout}s</Label>
                <Slider
                  value={[settings.commandTimeout]}
                  onValueChange={([v]) =>
                    setSettings({ ...settings, commandTimeout: v })
                  }
                  min={10}
                  max={600}
                  step={10}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Sub-agents</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow the agent to spawn sub-agents for parallel tasks
                  </p>
                </div>
                <Switch
                  checked={settings.enableAgents}
                  onCheckedChange={(v) =>
                    setSettings({ ...settings, enableAgents: v })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Denied Command Prefixes</Label>
                <Textarea
                  value={settings.deniedCommandPrefixes}
                  onChange={(e) =>
                    setSettings({ ...settings, deniedCommandPrefixes: e.target.value })
                  }
                  placeholder="rm -rf /,dd if=,mkfs"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Comma-separated list of command prefixes the agent cannot run
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workspace Settings */}
        <TabsContent value="workspace" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Workspace Configuration</CardTitle>
              <CardDescription>
                Paths and directories for the agent's workspace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Default Workspace Path</Label>
                <Input
                  value={settings.defaultWorkspace}
                  onChange={(e) =>
                    setSettings({ ...settings, defaultWorkspace: e.target.value })
                  }
                  placeholder="/home/jay"
                />
              </div>

              <div className="space-y-2">
                <Label>Workspace Allowlist</Label>
                <Textarea
                  value={settings.workspaceAllowlist}
                  onChange={(e) =>
                    setSettings({ ...settings, workspaceAllowlist: e.target.value })
                  }
                  placeholder="/home/jay"
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  Comma-separated list of directories the agent can access
                </p>
              </div>

              <div className="space-y-2">
                <Label>Model Directories</Label>
                <Textarea
                  value={settings.modelDirs}
                  onChange={(e) =>
                    setSettings({ ...settings, modelDirs: e.target.value })
                  }
                  placeholder="/home/jay/models"
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  Colon-separated directories to scan for local model files
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys */}
        <TabsContent value="api" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>API Keys</CardTitle>
              <CardDescription>
                External service integrations. Keys are stored in your browser only.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Gateway passcode
                </Label>
                <Input
                  type="password"
                  autoComplete="off"
                  value={gatewayPasscode}
                  onChange={(e) => setGatewayPasscode(e.target.value)}
                  placeholder="Same value as AUTH_API_KEYS on the gateway"
                />
                <p className="text-xs text-muted-foreground">
                  Sent as the <code className="text-xs">x-api-key</code> header on REST and on the WebSocket{' '}
                  <code className="text-xs">CONNECT</code> when{' '}
                  <code className="text-xs">AUTH_ENABLED=true</code>. Stored in this browser only. Clear the field
                  and save to remove it.
                </p>
              </div>

              <div className="space-y-2">
                <Label>OpenAI API Key (Optional Fallback)</Label>
                <Input
                  type="password"
                  value={settings.openaiKey}
                  onChange={(e) =>
                    setSettings({ ...settings, openaiKey: e.target.value })
                  }
                  placeholder="sk-..."
                />
                <p className="text-xs text-muted-foreground">
                  For fallback when local models are unavailable
                </p>
              </div>

              <div className="space-y-2">
                <Label>Discord Bot Token</Label>
                <Input
                  type="password"
                  value={settings.discordToken}
                  onChange={(e) =>
                    setSettings({ ...settings, discordToken: e.target.value })
                  }
                  placeholder="Bot token"
                />
              </div>

              <div className="space-y-2">
                <Label>Telegram Bot Token</Label>
                <Input
                  type="password"
                  value={settings.telegramToken}
                  onChange={(e) =>
                    setSettings({ ...settings, telegramToken: e.target.value })
                  }
                  placeholder="Token from @BotFather"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System */}
        <TabsContent value="system" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>System Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Auto-start on Boot</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically start OMTAE services on system boot
                  </p>
                </div>
                <Switch
                  checked={settings.autoStart}
                  onCheckedChange={(v) =>
                    setSettings({ ...settings, autoStart: v })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Log Level</Label>
                <Select
                  value={settings.logLevel}
                  onValueChange={(v) =>
                    setSettings({ ...settings, logLevel: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debug">Debug</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Log History (days): {settings.maxLogHistory}</Label>
                <Slider
                  value={[settings.maxLogHistory]}
                  onValueChange={([v]) =>
                    setSettings({ ...settings, maxLogHistory: v })
                  }
                  min={7}
                  max={90}
                  step={7}
                />
              </div>
            </CardContent>
          </Card>

          {/* Gateway Health */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Gateway Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              {health ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <div className="flex items-center gap-1">
                      {health.status === 'healthy' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-amber-500" />
                      )}
                      <span className="font-medium">{health.status}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Version</p>
                    <p className="font-medium">{health.version || 'unknown'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Uptime</p>
                    <p className="font-medium">
                      {health.uptime ? `${Math.floor(health.uptime / 3_600_000)}h ${Math.floor((health.uptime % 3_600_000) / 60_000)}m` : 'N/A'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Redis</p>
                    <div className="flex items-center gap-1">
                      {health.checks?.redis ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <span className="font-medium">{health.checks?.redis ? 'connected' : 'disconnected'}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4">
                  Unable to reach gateway health endpoint.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
